<script setup lang="ts">
  import "@/assets/css/vendors/simplebar.css";
  import "@/assets/css/themes/echo.css";
  import { useRoute, useRouter } from "vue-router";
  import Lucide from "@/components/Base/Lucide";
  import Breadcrumb from "@/components/Base/Breadcrumb";
  import { Menu } from "@/components/Base/Headless";
  import { useMenuStore } from "@/stores/menu";
  import { useCompactMenuStore } from "@/stores/compact-menu";
  import {
    type ProvideForceActiveMenu,
    forceActiveMenu,
    type Route,
    type FormattedMenu,
    nestedMenu,
    linkTo,
    enter,
    leave,
  } from "./side-menu";
  import { watch, reactive, ref, computed, onMounted, provide } from "vue";
  import users from "@/fakers/users";
  import SimpleBar from "simplebar";
  import QuickSearch from "@/components/QuickSearch";
  import SwitchAccount from "@/components/SwitchAccount";
  import NotificationsPanel from "@/components/NotificationsPanel";
  import ActivitiesPanel from "@/components/ActivitiesPanel";



  const compactMenu = useCompactMenuStore();
  const setCompactMenu = (val: boolean) => {
    compactMenu.setCompactMenu(val);
  };
  const quickSearch = ref(false);
  const setQuickSearch = (value: boolean) => {
    quickSearch.value = value;
  };

  const switchAccount = ref(false);
  const setSwitchAccount = (value: boolean) => {
    switchAccount.value = value;
  };

  const notificationsPanel = ref(false);
  const setNotificationsPanel = (value: boolean) => {
    notificationsPanel.value = value;
  };

  const activitiesPanel = ref(false);
  const setActivitiesPanel = (value: boolean) => {
    activitiesPanel.value = value;
  };

  const compactMenuOnHover = ref(false);
  const route: Route = useRoute();
  const router = useRouter();

  // console.log(router)
  let formattedMenu = reactive<Array<FormattedMenu | string>>([]);
  const setFormattedMenu = (
    computedFormattedMenu: Array<FormattedMenu | string>
  ) => {
    Object.assign(formattedMenu, computedFormattedMenu);
  };
  const menuStore = useMenuStore();
  const menu = computed(() => nestedMenu(menuStore.fullMenu, route));

  provide<ProvideForceActiveMenu>("forceActiveMenu", (pageName: string) => {
    forceActiveMenu(route, pageName);
    setFormattedMenu(menu.value);
  });

  const scrollableRef = ref<HTMLDivElement>();

  const topBarActive = ref(false);

  const toggleCompactMenu = (event: MouseEvent) => {
    event.preventDefault();
    setCompactMenu(!compactMenu.value);
  };

  const compactLayout = () => {
    if (window.innerWidth <= 1600) {
      setCompactMenu(true);
    }
  };

  const requestFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  watch(menu, () => {
    setFormattedMenu(menu.value);
  });

  watch(
    computed(() => route.path),
    () => {
      delete route.forceActiveMenu;
    }
  );


  onMounted(() => {
    if (scrollableRef.value) {
      new SimpleBar(scrollableRef.value);
    }

    setFormattedMenu(menu.value);

    compactLayout();

    window.onresize = () => {
      compactLayout();
    };
  });

  window.onscroll = () => {
    // Topbar
    if (document.body.scrollTop > 0 || document.documentElement.scrollTop > 0) {
      topBarActive.value = true;
    } else {
      topBarActive.value = false;
    }
  };


  import { useAuthStore } from "@/stores/app/AuthStore";
  const authStore = useAuthStore();

  const windowWidth = ref(window.innerWidth);

  const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
  };

  window.addEventListener('resize', updateWindowWidth);



  const computedBreadcrumbs = computed(() => {
    // Initialize an array with "Home"


    var defaultBreadcrumbs = [
      { label: 'Home', link: '/' }
    ];

    if(windowWidth.value < 1280 && route.name == 'landing-page') {
      defaultBreadcrumbs = [];
    }



    let routeBreadcrumbs = [];

    // Check if `route.meta.breadcrumb` is a function or an array
    if (route.meta.breadcrumb) {
      if (typeof route.meta.breadcrumb === 'function') {
        routeBreadcrumbs = route.meta.breadcrumb(route);
      } else if (Array.isArray(route.meta.breadcrumb)) {
        routeBreadcrumbs = route.meta.breadcrumb;
      }
    }

    // Combine the default "Home" breadcrumb with the route-specific breadcrumbs
    const breadcrumbs = [...defaultBreadcrumbs, ...routeBreadcrumbs];

    // Set the last breadcrumb as active, or the only item as active if it's the only one
    return breadcrumbs.map((crumb, index) => ({
      ...crumb,
      active: breadcrumbs.length > 1 && index === breadcrumbs.length - 1
    }));
  });

  // Watch for changes to the route or the breadcrumb meta field
  watch(
      () => route.meta.breadcrumb,
      () => {
        computedBreadcrumbs.value = computedBreadcrumbs.value;
      },
      { immediate: true }
  );




</script>

<template>
  <div
    :class="[
      'echo group bg-gradient-to-b from-slate-200/70 to-slate-50 background relative min-h-screen',
      'before:content-[\'\'] before:h-[370px] before:w-screen before:bg-gradient-to-t before:from-theme-1/80 before:to-theme-2 [&.background--hidden]:before:opacity-0 before:transition-[opacity,height] before:ease-in-out before:duration-300 before:top-0 before:fixed',
      'after:content-[\'\'] after:h-[370px] after:w-screen [&.background--hidden]:after:opacity-0 after:transition-[opacity,height] after:ease-in-out after:duration-300 after:top-0 after:fixed after:bg-texture-white after:bg-contain after:bg-fixed after:bg-[center_-13rem] after:bg-no-repeat',
      { 'background--hidden': topBarActive || route.name === 'ticketing-orders-bulk-import' || route.name === 'ticketing-orders-bulk-send-email' },
    ]"
  >
    <div
      :class="[
        'xl:ml-0 shadow-xl transition-[margin,padding] duration-300 xl:shadow-none fixed top-0 left-0 z-50 side-menu group inset-y-0 xl:py-3.5 xl:pl-3.5',
        'after:content-[\'\'] after:fixed after:inset-0 after:bg-black/80 after:xl:hidden',
        { 'side-menu--collapsed': compactMenu.value },
        { 'side-menu--on-hover': compactMenuOnHover },
        { 'ml-0 after:block': menuStore.activeMobileMenu },
        { '-ml-[275px] after:hidden': !menuStore.activeMobileMenu },
      ]"
    >
      <div
        :class="[
          'fixed ml-[275px] w-10 h-10 items-center justify-center xl:hidden z-50',
          { flex: menuStore.activeMobileMenu },
          { hidden: !menuStore.activeMobileMenu },
        ]"
      >
        <a
          href=""
          @click="
            (event) => {
              event.preventDefault();
              menuStore.activeMobileMenu = false;
            }
          "
          class="mt-5 ml-5"
        >
          <Lucide icon="X" class="w-8 h-8 text-white" />
        </a>
      </div>
      <div
        :class="[
          'h-full box bg-white/[0.95] rounded-none xl:rounded-xl z-20 relative w-[275px] duration-300 transition-[width] group-[.side-menu--collapsed]:xl:w-[91px] group-[.side-menu--collapsed.side-menu--on-hover]:xl:shadow-[6px_0_12px_-4px_#0000000f] group-[.side-menu--collapsed.side-menu--on-hover]:xl:w-[275px] overflow-hidden flex flex-col',
        ]"
        @mouseover="
          (event) => {
            event.preventDefault();
            compactMenuOnHover = true;
          }
        "
        @mouseleave="
          (event) => {
            event.preventDefault();
            compactMenuOnHover = false;
          }
        "
      >
        <div
          :class="[
            'flex-none hidden xl:flex items-center z-10 px-5 h-[65px] w-[275px] overflow-hidden relative duration-300 group-[.side-menu--collapsed]:xl:w-[91px] group-[.side-menu--collapsed.side-menu--on-hover]:xl:w-[275px]',
          ]"
        >
          <RouterLink
            to="/"
            class="flex items-center transition-[margin] duration-300 group-[.side-menu--collapsed]:xl:ml-2 group-[.side-menu--collapsed.side-menu--on-hover]:xl:ml-0"
          >
            <div
              class="logo-wrapper flex items-center justify-center rounded-lg h-[34px] transition-transform ease-in-out"
            >
              <!-- <img src="/src/assets/images/projects/project1-400x400.jpg"/> -->
            <svg class="logo" xmlns="http://www.w3.org/2000/svg" width="140" height="139" viewBox="0 0 140 139" fill="none">
                  <g clip-path="url(#clip0_1010_104)">
                    <path d="M7.51001 61.2605L5.95001 61.0005L6.70001 56.5405L8.26001 56.8005L7.51001 61.2605Z" fill="#F2F2F2"></path>
                    <path d="M10.27 42.0306C11.22 40.5406 12.5 39.6306 14.13 40.3106C15.84 41.0206 16.49 42.8806 15.01 46.3806C14.01 48.7606 12.57 50.5206 10.55 50.8606L10.89 48.4706C11.48 48.3506 12.71 47.6306 13.49 45.7806C13.9 44.8006 14.46 42.8706 13.36 42.4006C12.62 42.0906 12.11 42.6806 11.57 43.5306L9.54998 46.7406C8.68998 48.1206 7.47998 48.8906 5.79998 48.1906C4.12998 47.4906 3.93998 45.4306 5.07998 42.7306C6.43998 39.5206 8.15998 38.6906 9.34998 38.5806L8.98998 40.9006C8.33998 41.0106 7.21998 41.5106 6.51998 43.1706C5.84998 44.7606 5.97998 45.8106 6.66998 46.1106C7.38998 46.4106 7.84998 45.8306 8.32998 45.0706L10.26 42.0406L10.27 42.0306Z" fill="#F2F2F2"></path>
                    <path d="M23.48 32.2604C21.67 34.4804 19.46 35.8204 17.2 34.0004L12.2 29.9704L13.57 28.2904L18.48 32.2504C19.6 33.1504 21.1 32.7704 22.3 31.3004C23.62 29.6804 23.66 28.2604 22.53 27.3504L17.58 23.3604L18.96 21.6704L23.95 25.6904C26.05 27.3804 25.68 29.5504 23.47 32.2604" fill="#F2F2F2"></path>
                    <path d="M40.11 17.7002L38.7 15.6002C37.92 14.4302 36.8 12.7102 36.36 11.9802L36.34 12.0002C36.43 12.4402 36.6 13.5102 36.77 14.8302L37.37 19.5202L35.95 20.4602L32.26 18.4102C30.75 17.5702 29.58 16.8202 29.33 16.6402L29.31 16.6602C29.75 17.2602 30.93 18.9502 31.73 20.1402L33.18 22.3002L31.34 23.5202L25.81 15.2702L27.97 13.8402L32.33 16.2102C33.84 17.0302 35.04 17.7802 35.32 17.9902L35.34 17.9702C35.26 17.6702 35.01 16.1302 34.82 14.4302L34.28 9.64021L36.43 8.22021L41.96 16.4702L40.12 17.6902L40.11 17.7002Z" fill="#F2F2F2"></path>
                    <path d="M57.8 11.0904L57.12 8.66043C56.74 7.31043 56.22 5.32043 56.04 4.49043H56.01C55.96 4.94043 55.78 6.02043 55.52 7.32043L54.61 11.9604L52.96 12.4104L50.11 9.31043C48.94 8.04043 48.07 6.96043 47.88 6.72043H47.85C48.08 7.43043 48.67 9.41043 49.05 10.7904L49.74 13.2904L47.61 13.8704L44.96 4.31043L47.46 3.63043L50.86 7.24043C52.03 8.49043 52.94 9.58043 53.14 9.86043H53.17C53.19 9.54043 53.44 8.00043 53.79 6.33043L54.79 1.62043L57.28 0.94043L59.93 10.5004L57.8 11.0804V11.0904Z" fill="#F2F2F2"></path>
                    <path d="M66.46 0.050293L66.24 9.96029L76.16 10.1803L76.19 8.45029L68.5 8.28029L68.56 5.72029L75.14 5.86029L75.17 4.22029L68.59 4.08029L68.64 1.77029L75.96 1.93029L76 0.250293L66.46 0.050293Z" fill="#F2F2F2"></path>
                    <path d="M89.68 4.74037L86.58 3.83037L85.86 6.26037L88.97 7.18037C90.16 7.53037 90.99 7.42037 91.28 6.47037C91.55 5.57037 90.85 5.08037 89.68 4.74037ZM90.16 9.18037L91.68 13.9604L89.23 13.2404L87.87 8.57037L85.38 7.84037L84.21 11.7704L82.08 11.1404L84.91 1.63037L90.73 3.35037C92.79 3.96037 93.96 5.54037 93.47 7.18037C93.01 8.72037 91.54 9.32037 90.15 9.19037" fill="#F2F2F2"></path>
                    <path d="M106.57 18.8906L110.64 13.3506L112.42 14.6406L108.33 20.2106C106.84 22.2306 105.82 22.2106 103.53 20.5506L102.69 19.9406L103.69 18.5806C105.45 19.8606 105.77 19.9706 106.56 18.9006" fill="#F2F2F2"></path>
                    <path d="M116.58 26.4904L118.92 29.1704L120.64 25.3004L120.62 25.2804L116.58 26.4804V26.4904ZM121.56 23.1104L123.07 24.8404L118.8 35.0804L117.18 33.2304L118.2 30.8704L114.81 26.9904L112.33 27.6704L110.82 25.9404L121.56 23.1204V23.1104Z" fill="#F2F2F2"></path>
                    <path d="M126.01 49.2201L128.33 48.2001C129.62 47.6301 131.52 46.8301 132.32 46.5301V46.5001C131.86 46.5101 130.76 46.5001 129.43 46.4301L124.68 46.2101L123.99 44.6501L126.65 41.3901C127.74 40.0601 128.69 39.0401 128.9 38.8201V38.7901C128.22 39.1101 126.34 39.9901 125.02 40.5701L122.64 41.6201L121.75 39.6101L130.87 35.6001L131.92 37.9601L128.83 41.8301C127.76 43.1701 126.81 44.2201 126.55 44.4601V44.4901C126.88 44.4601 128.44 44.4901 130.15 44.5801L134.98 44.8701L136.02 47.2201L126.9 51.2301L126.01 49.2201Z" fill="#F2F2F2"></path>
                    <path d="M132.753 56.615L131.194 56.8726L131.931 61.3321L133.49 61.0746L132.753 56.615Z" fill="#F2F2F2"></path>
                    <path d="M3.77003 83.4803C2.01003 82.7703 0.450029 81.1002 0.0700295 77.8602C-0.34997 74.3202 1.26003 71.2603 4.65003 70.8703C8.71003 70.4003 10.42 74.0103 10.73 76.6003C11.06 79.3903 10.15 81.7203 8.53003 82.8103L7.60003 80.7003C8.51003 80.1103 9.38003 78.9203 9.12003 76.8103C8.82003 74.2303 7.18003 72.9602 4.93003 73.2202C2.70003 73.4802 1.44003 75.2802 1.72003 77.6402C1.97003 79.7402 3.00003 80.6803 4.09003 81.1703L3.78003 83.4803H3.77003Z" fill="#F2F2F2"></path>
                    <path d="M7.09001 100.47L11.07 98.7202L8.47001 92.8502L4.48001 94.6102L3.57001 92.5502L12.84 88.4702L13.75 90.5302L10.08 92.1402L12.69 98.0102L16.36 96.3902L17.27 98.4402L7.99001 102.51L7.09001 100.47Z" fill="#F2F2F2"></path>
                    <path d="M18.3 110.39L20.65 113.15L22.44 109.24L22.42 109.22L18.29 110.4L18.3 110.39ZM23.4 107.01L24.92 108.79L20.46 119.15L18.84 117.25L19.9 114.86L16.5 110.87L13.97 111.53L12.45 109.75L23.41 107L23.4 107.01Z" fill="#F2F2F2"></path>
                    <path d="M36 130.511L37.38 128.34C38.15 127.141 39.3 125.391 39.8 124.681H39.78C39.41 124.921 38.47 125.531 37.31 126.231L33.17 128.721L31.7 127.791L32.1 123.531C32.26 121.791 32.48 120.401 32.54 120.091L32.52 120.071C32.14 120.721 31.05 122.521 30.26 123.751L28.84 125.981L26.94 124.791L32.37 116.271L34.6 117.671L34.19 122.681C34.05 124.421 33.84 125.841 33.76 126.181L33.79 126.201C34.04 126.001 35.38 125.131 36.88 124.261L41.13 121.791L43.35 123.191L37.92 131.711L36.02 130.511H36Z" fill="#F2F2F2"></path>
                    <path d="M53.48 128.41L51.05 127.74L50.27 130.56L52.8 131.25C54.29 131.66 55.22 131.51 55.53 130.41C55.78 129.52 55.32 128.9 53.49 128.4M53.04 133.05L49.83 132.17L48.81 135.85L46.63 135.25L49.33 125.53L54.58 126.97C57.4 127.74 58.23 129.45 57.8 131.01C57.29 132.84 55.73 133.79 53.04 133.05Z" fill="#F2F2F2"></path>
                    <path d="M64.9707 128.734L64.367 138.806L66.623 138.941L67.2266 128.869L64.9707 128.734Z" fill="#F2F2F2"></path>
                    <path d="M81.35 136.66C83.81 136.24 85.24 134.65 84.84 132.33C84.45 130.07 82.62 128.94 80.1 129.37C77.33 129.84 76.37 131.71 76.72 133.71C77.1 135.95 78.96 137.07 81.36 136.66M79.83 127.77C82.44 127.33 86.49 127.88 87.18 131.91C87.85 135.81 84.55 137.78 81.65 138.27C78.75 138.76 75.04 137.92 74.38 134.1C73.68 130.03 77.34 128.19 79.83 127.77Z" fill="#F2F2F2"></path>
                    <path d="M104.47 129.91L98.89 127.89C96.49 127.02 95.69 126.7 95.33 126.53H95.3C95.5 126.88 96.73 129.14 97.57 130.77L98.65 132.88L96.66 133.89L92.05 124.9L93.88 123.97L99.49 126.03C101.4 126.73 102.44 127.12 103 127.35H103.03C102.52 126.48 101.97 125.46 100.97 123.53L99.68 121.03L101.68 120.01L106.29 129L104.46 129.93L104.47 129.91Z" fill="#F2F2F2"></path>
                    <path d="M115.48 114.11C116.98 113.11 118.55 112.79 119.79 114.09C121.08 115.45 120.93 117.45 118.1 120.1C116.18 121.9 114.11 122.95 112.1 122.45L113.41 120.36C114.01 120.49 115.45 120.33 116.95 118.92C117.74 118.18 119.06 116.6 118.22 115.72C117.66 115.13 116.95 115.47 116.09 116.04L112.88 118.2C111.51 119.13 110.07 119.35 108.79 118.01C107.53 116.68 108.2 114.68 110.38 112.63C112.97 110.19 114.92 110.13 116.07 110.52L114.77 112.53C114.12 112.37 112.87 112.37 111.53 113.63C110.24 114.84 109.93 115.87 110.46 116.42C111.01 117 111.67 116.65 112.44 116.13L115.49 114.1L115.48 114.11Z" fill="#F2F2F2"></path>
                    <path d="M131.83 101.62L128.06 99.4401L124.81 104.98L128.58 107.16L127.44 109.11L118.68 104.03L119.82 102.09L123.29 104.1L126.53 98.5601L123.07 96.5501L124.2 94.6201L132.96 99.7001L131.83 101.62Z" fill="#F2F2F2"></path>
                    <path d="M136.12 91.3804L126.43 88.3604L127.11 86.2104L136.79 89.2304L136.12 91.3804Z" fill="#F2F2F2"></path>
                    <path d="M130.92 75.1606L130.68 77.6606L133.61 77.9406L133.86 75.3406C134.01 73.8106 133.7 72.9206 132.56 72.8106C131.64 72.7206 131.1 73.2806 130.92 75.1606ZM135.6 74.7906L135.28 78.0906L139.1 78.4506L138.89 80.6906L128.79 79.7406L129.3 74.3406C129.58 71.4406 131.12 70.3306 132.74 70.4806C134.64 70.6606 135.85 72.0206 135.59 74.7906" fill="#F2F2F2"></path>
                    <path d="M92.45 78.4004C91.01 76.3404 89.32 73.5704 87.66 70.5704C88.87 66.8404 89.72 63.0104 90.07 59.1704C91.39 61.9304 93.31 65.4304 95.94 69.0204C95.82 69.4704 95.68 69.9304 95.53 70.3704C94.57 73.1904 93.53 75.8604 92.46 78.3904M43.51 69.0104C46.13 65.4204 48.06 61.9104 49.38 59.1604C49.73 63.0104 50.58 66.8304 51.79 70.5604C50.13 73.5504 48.44 76.3204 47 78.3804C45.92 75.8404 44.88 73.1804 43.93 70.3604C43.78 69.9204 43.64 69.4604 43.52 69.0104M46.62 52.5204C47.14 53.0304 47.93 53.4704 48.92 53.8404C48.19 55.7904 46.25 60.4804 42.85 65.5804C42.22 60.5004 43.25 55.4004 46.63 52.5104M69.75 48.5604C81.96 48.5604 89.05 50.1004 90.76 51.1004C89.05 52.0904 81.96 53.6404 69.75 53.6404C57.54 53.6404 50.45 52.0904 48.74 51.1004C50.45 50.1104 57.54 48.5604 69.75 48.5604ZM55.89 38.2804L64.53 43.0404L69.9 33.0604L74.95 43.0604L83.62 38.2804L81.12 46.5604C76.29 46.1004 71.5 46.0604 69.75 46.0604C68 46.0604 63.22 46.1104 58.38 46.5604L55.88 38.2804H55.89ZM81.27 79.6504C79.66 76.9104 78.01 73.8804 76.51 70.7504C77.99 67.1804 79.2 63.6304 79.94 60.2804C81.25 63.5604 83.05 67.2904 84.97 70.8204C83.93 73.8504 82.68 76.8104 81.27 79.6604M82.8 82.1904C84.2 79.4704 85.49 76.6304 86.59 73.7204C88.28 76.6404 89.94 79.2404 91.31 81.0704C90.03 83.8904 88.7 86.5104 87.38 88.9104C86.13 87.3004 84.52 84.9704 82.8 82.2004M77.2 91.7804C78.64 89.5604 80.08 87.2104 81.44 84.7404C83.26 87.5704 84.89 89.8404 86.05 91.2704C84.52 93.9104 83.03 96.2404 81.66 98.2504C80.43 96.5704 78.88 94.3604 77.21 91.7904M75.95 105.81C74.75 104.37 73.15 102.44 71.34 100.11C72.73 98.2804 74.2 96.2504 75.7 94.0604C77.41 96.6504 78.95 98.8304 80.14 100.43C78.35 102.91 76.87 104.74 75.95 105.82M63.81 94.0704C65.3 96.2604 66.78 98.2904 68.17 100.12C66.36 102.45 64.76 104.38 63.56 105.82C62.64 104.74 61.16 102.92 59.37 100.43C60.56 98.8404 62.1 96.6504 63.81 94.0604M58.06 84.7604C59.42 87.2304 60.86 89.5804 62.3 91.8004C60.62 94.3804 59.08 96.5804 57.85 98.2704C56.48 96.2604 54.99 93.9304 53.46 91.2904C54.62 89.8604 56.25 87.5904 58.07 84.7604M56.71 82.2204C54.98 85.0004 53.37 87.3204 52.13 88.9404C50.81 86.5404 49.48 83.9104 48.2 81.1004C49.57 79.2804 51.23 76.6804 52.92 73.7504C54.02 76.6604 55.31 79.5004 56.71 82.2204ZM59.57 60.3104C60.31 63.6604 61.52 67.2104 63 70.7804C61.5 73.9104 59.85 76.9504 58.24 79.6804C56.83 76.8304 55.57 73.8704 54.54 70.8404C56.45 67.3104 58.26 63.5804 59.57 60.3004M64.43 67.6404C62.86 63.5304 61.74 59.5104 61.38 55.9104C64.07 56.0804 66.54 56.1504 68.22 56.1704C67.5 59.7804 66.12 63.7404 64.42 67.6404M71.28 56.1704C72.96 56.1504 75.43 56.0904 78.12 55.9104C77.77 59.5204 76.64 63.5304 75.07 67.6404C73.37 63.7304 71.99 59.7804 71.27 56.1704M69.74 79.2004C68.3 76.4804 66.93 73.6804 65.73 70.8904C67.33 67.4604 68.74 63.9104 69.74 60.4804C70.74 63.9104 72.15 67.4504 73.75 70.8904C72.55 73.6904 71.18 76.4904 69.74 79.2004ZM71.16 81.8204C72.57 79.2204 73.94 76.5304 75.17 73.8204C76.7 76.8604 78.33 79.7404 79.9 82.3104C78.56 84.8104 77.14 87.2104 75.7 89.4704C74.21 87.1104 72.67 84.5304 71.16 81.8204ZM63.78 89.4704C62.34 87.2104 60.92 84.8104 59.58 82.3104C61.15 79.7404 62.78 76.8504 64.31 73.8204C65.55 76.5404 66.91 79.2304 68.32 81.8204C66.81 84.5304 65.26 87.1104 63.78 89.4704ZM69.74 84.3804C71.24 87.0204 72.76 89.5104 74.2 91.7804C72.68 94.0504 71.17 96.1504 69.74 98.0504C68.31 96.1504 66.8 94.0504 65.28 91.7804C66.72 89.5204 68.24 87.0204 69.74 84.3804ZM51.74 54.7004C53.78 55.1604 56.15 55.4704 58.51 55.6904C57.62 58.8504 55.68 63.2004 53.47 67.4604C52.31 63.2504 51.66 58.9604 51.74 54.7004ZM86 67.4604C83.79 63.2004 81.85 58.8504 80.96 55.6904C83.32 55.4704 85.69 55.1504 87.73 54.7004C87.82 58.9604 87.17 63.2604 86 67.4604ZM96.62 65.6304C93.22 60.5304 91.28 55.8304 90.55 53.8804C91.54 53.5004 92.33 53.0704 92.85 52.5604C96.23 55.4504 97.26 60.5504 96.63 65.6304M91.81 48.9304C89.88 47.9604 86.93 47.3204 83.65 46.8904L87.79 33.1504L76 39.6504L69.95 27.6704L63.5 39.6704L51.68 33.1504L55.83 46.8904C52.55 47.3104 49.66 47.9404 47.67 48.9304C39.93 52.5504 38.73 62.8704 41.54 71.1904C49.41 94.4504 62.2 108.13 62.74 108.71L63.73 109.76L69.74 102.33L75.74 109.76L76.73 108.71C77.27 108.14 90.06 94.4504 97.93 71.2004C100.75 62.8704 99.55 52.5504 91.81 48.9304Z" fill="#F2F2F2"></path>
                  </g>
                  <defs>
                    <clipPath id="clip0_1010_104">
                      <rect width="139.11" height="138.88" fill="white" transform="translate(0 0.050293)"></rect>
                    </clipPath>
                  </defs>
                </svg>            
              <!-- <div
                class="w-[16px] h-[16px] relative -rotate-45 [&_div]:bg-white"
              >
                <div
                  class="absolute w-[21%] left-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]"
                ></div>
                <div
                  class="absolute w-[21%] inset-0 m-auto h-[120%] rounded-full"
                ></div>
                <div
                  class="absolute w-[21%] right-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]"
                ></div>
              </div> -->
            </div>
            <div
              class="ml-3.5 group-[.side-menu--collapsed.side-menu--on-hover]:xl:opacity-100 group-[.side-menu--collapsed]:xl:opacity-0 transition-opacity font-medium text-nowrap"
            >
              Summer Jam
            </div>
          </RouterLink>
          <a
            href=""
            @click="toggleCompactMenu"
            class="hidden group-[.side-menu--collapsed.side-menu--on-hover]:xl:opacity-100 group-[.side-menu--collapsed]:xl:rotate-180 group-[.side-menu--collapsed]:xl:opacity-0 transition-[opacity,transform] 3xl:flex items-center justify-center w-[20px] h-[20px] ml-auto border rounded-full border-slate-600/40 hover:bg-slate-600/5"
          >
            <Lucide icon="ArrowLeft" class="w-3.5 h-3.5 stroke-[1.3]" />
          </a>
        </div>


        <div
          ref="scrollableRef"
          :class="[
            'w-full h-full z-20 px-5 overflow-y-auto overflow-x-hidden pb-3 [-webkit-mask-image:-webkit-linear-gradient(top,rgba(0,0,0,0),black_30px)] [&:-webkit-scrollbar]:w-0 [&:-webkit-scrollbar]:bg-transparent',
            '[&_.simplebar-content]:p-0 [&_.simplebar-track.simplebar-vertical]:w-[10px] [&_.simplebar-track.simplebar-vertical]:mr-0.5 [&_.simplebar-track.simplebar-vertical_.simplebar-scrollbar]:before:bg-slate-400/30',
          ]"
        >
          <ul class="scrollable">

            <!-- BEGIN: First Child -->
            <template v-for="(menu, menuKey) in formattedMenu">
              <li
                v-if="typeof menu === 'string'"
                class="side-menu__divider"
                :key="'divider-' + menuKey"
              >
                {{ menu }}
              </li>
              <li v-else :key="menuKey">
                <a
                  href=""
                  :class="[
                    'side-menu__link',
                    { 'side-menu__link--active': menu.active },
                    {
                      'side-menu__link--active-dropdown': menu.activeDropdown,
                    },
                  ]"
                  @click="(event: MouseEvent) => {
                    event.preventDefault();
                    linkTo(menu, router);
                    setFormattedMenu([...formattedMenu]);
                  }"
                >
                  <Lucide :icon="menu.icon" class="side-menu__link__icon" />
                  <div class="side-menu__link__title">{{ menu.title }}</div>
                  <div v-if="menu.badge" class="side-menu__link__badge">
                    {{ menu.badge }}
                  </div>
                  <Lucide
                    v-if="menu.subMenu"
                    icon="ChevronDown"
                    class="side-menu__link__chevron"
                  />
                </a>
                <!-- BEGIN: Second Child -->
                <Transition @enter="enter" @leave="leave">
                  <ul v-if="menu.subMenu && menu.activeDropdown">
                    <li
                      v-for="(subMenu, subMenuKey) in menu.subMenu"
                      :key="subMenuKey"
                    >
                      <a
                        href=""
                        :class="[
                          'side-menu__link',
                          { 'side-menu__link--active': subMenu.active },
                          {
                            'side-menu__link--active-dropdown':
                              subMenu.activeDropdown,
                          },
                        ]"
                        @click="(event: MouseEvent) => {
                          event.preventDefault();
                          linkTo(subMenu, router);
                          setFormattedMenu([...formattedMenu]);
                        }"
                      >
                        <Lucide
                          :icon="subMenu.icon"
                          class="side-menu__link__icon"
                        />
                        <div class="side-menu__link__title">
                          {{ subMenu.title }}
                        </div>
                        <div
                          v-if="subMenu.badge"
                          class="side-menu__link__badge"
                        >
                          {{ subMenu.badge }}
                        </div>
                        <Lucide
                          v-if="subMenu.subMenu"
                          icon="ChevronDown"
                          class="side-menu__link__chevron"
                        />
                      </a>
                      <!-- BEGIN: Third Child -->
                      <Transition @enter="enter" @leave="leave">
                        <ul v-if="subMenu.subMenu && subMenu.activeDropdown">
                          <li
                            v-for="(
                              lastSubMenu, lastSubMenuKey
                            ) in subMenu.subMenu"
                            :key="lastSubMenuKey"
                          >
                            <a
                              href=""
                              :class="[
                                'side-menu__link',
                                {
                                  'side-menu__link--active': lastSubMenu.active,
                                },
                                {
                                  'side-menu__link--active-dropdown':
                                    lastSubMenu.activeDropdown,
                                },
                              ]"
                              @click="(event: MouseEvent) => {
                                event.preventDefault();
                                linkTo(lastSubMenu, router);
                                setFormattedMenu([...formattedMenu]);
                              }"
                            >
                              <Lucide
                                :icon="lastSubMenu.icon"
                                class="side-menu__link__icon"
                              />
                              <div class="side-menu__link__title">
                                {{ lastSubMenu.title }}
                              </div>
                              <div
                                v-if="lastSubMenu.badge"
                                class="side-menu__link__badge"
                              >
                                {{ lastSubMenu.badge }}
                              </div>
                            </a>
                          </li>
                        </ul>
                      </Transition>
                      <!-- END: Third Child -->
                    </li>
                  </ul>
                </Transition>
                <!-- END: Second Child -->
              </li>
            </template>
            <!-- END: First Child -->
          </ul>
        </div>
      </div>
      <div
        class="fixed h-[65px] transition-[margin] duration-100 xl:ml-[275px] group-[.side-menu--collapsed]:xl:ml-[90px] mt-3.5 inset-x-0 top-0"
      >
        <div
          :class="[
            'top-bar absolute left-0 xl:left-3.5 right-0 h-full mx-5 group',
            'before:content-[\'\'] before:absolute before:top-0 before:inset-x-0 before:-mt-[15px] before:h-[20px] before:backdrop-blur',
            { 'top-bar--active': topBarActive || route.name === 'ticketing-orders-bulk-import' || route.name === 'ticketing-orders-bulk-send-email' },
          ]"
        >
          <div
            class="container flex items-center w-full h-full transition-[padding,background-color,border-color] ease-in-out duration-300 box bg-transparent border-transparent shadow-none group-[.top-bar--active]:box group-[.top-bar--active]:px-5 group-[.top-bar--active]:bg-transparent group-[.top-bar--active]:border-transparent group-[.top-bar--active]:bg-gradient-to-r group-[.top-bar--active]:from-theme-1 group-[.top-bar--active]:to-theme-2"
          >
            <div class="flex items-center gap-1 xl:hidden">
              <a
                href=""
                @click="
                  (event) => {
                    event.preventDefault();
                    menuStore.activeMobileMenu = true;
                  }
                "
                class="text-white rounded-full hover:bg-white/5"
              >
                <Lucide icon="AlignJustify" class="w-[18px] h-[18px]" />
              </a>
<!--              <a-->
<!--                href=""-->
<!--                class="p-2 text-white rounded-full hover:bg-white/5"-->
<!--                @click="-->
<!--                  (event) => {-->
<!--                    event.preventDefault();-->
<!--                    quickSearch = true;-->
<!--                  }-->
<!--                "-->
<!--              >-->
<!--                <Lucide icon="Search" class="w-[18px] h-[18px]" />-->
<!--              </a>-->
            </div>
            <!-- BEGIN: Breadcrumb -->
            <Breadcrumb light class="flex-1 hidden xl:block">
              <Breadcrumb.Link
                  v-for="(item, index) in computedBreadcrumbs"
                  :key="item.label + '-' + item.active"
                  :to="item.link"
                  :active="item.active"
                  :index="index"
              >
                {{ item.label }}
              </Breadcrumb.Link>
            </Breadcrumb>

            <!-- END: Breadcrumb -->
            <!-- BEGIN: Search -->
            <!-- <div
              class="relative justify-center flex-1 hidden xl:flex"
              @click="
                () => {
                  quickSearch = true;
                }
              "
            >
              <div
                class="bg-white/[0.12] border-transparent border w-[350px] flex items-center py-2 px-3.5 rounded-[0.5rem] text-white/60 cursor-pointer hover:bg-white/[0.15] transition-colors duration-300 hover:duration-100"
              >
                <Lucide icon="Search" class="w-[18px] h-[18px]" />
                <div class="ml-2.5 mr-auto">Quick search...</div>
                <div>⌘K</div>
              </div>
            </div>
            <QuickSearch
              :quickSearch="quickSearch"
              :setQuickSearch="setQuickSearch"
            /> -->
            <!-- END: Search -->
            <!-- BEGIN: Notification & User Menu -->
            <div class="flex justify-end flex-1">
              <!-- <div class="flex items-center gap-1 ml-auto">
                <a
                  href=""
                  class="p-2 rounded-full hover:bg-white/5"
                  @click="
                    (e) => {
                      e.preventDefault();
                      activitiesPanel = true;
                    }
                  "
                >
                  <Lucide
                    icon="LayoutGrid"
                    class="text-white w-[18px] h-[18px]"
                  />
                </a>
                <a
                  href=""
                  class="p-2 rounded-full hover:bg-white/5"
                  @click="
                    (e) => {
                      e.preventDefault();
                      requestFullscreen();
                    }
                  "
                >
                  <Lucide icon="Expand" class="text-white w-[18px] h-[18px]" />
                </a>
                <a
                  href=""
                  class="p-2 rounded-full hover:bg-white/5"
                  @click="
                    (e) => {
                      e.preventDefault();
                      notificationsPanel = true;
                    }
                  "
                >
                  <Lucide icon="Bell" class="text-white w-[18px] h-[18px]" />
                </a>
              </div> -->

              <Menu class="ml-5">
                <Menu.Button
                  class="overflow-hidden rounded-full w-[36px] h-[36px] border-[3px] border-white/[0.15] image-fit bg-white flex items-center justify-center"
                >
                <Lucide
                    icon="UserRound"
                    class="w-[20px] h-[20px] stroke-[2]"
                  />                
                  <!-- <img
                    alt="Tailwise - Admin Dashboard Template"
                    :src="users.fakeUsers()[0].photo"
                  /> -->
                </Menu.Button>
                <Menu.Items class="w-56 mt-1">
                  <!-- <Menu.Item
                    @click="
                      () => {
                        switchAccount = true;
                      }
                    "
                  >
                    <Lucide icon="ToggleLeft" class="w-4 h-4 mr-2" />
                    Switch Account
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    @click="
                      () => {
                        router.push({
                          name: 'settings',
                          query: { page: 'connected-services' },
                        });
                      }
                    "
                  >
                    <Lucide icon="Settings" class="w-4 h-4 mr-2" />
                    Connected Services
                  </Menu.Item>
                  <Menu.Item
                    @click="
                      () => {
                        router.push({
                          name: 'settings',
                          query: { page: 'email-settings' },
                        });
                      }
                    "
                  >
                    <Lucide icon="Inbox" class="w-4 h-4 mr-2" />
                    Email Settings
                  </Menu.Item>
                  <Menu.Item
                    @click="
                      () => {
                        router.push({
                          name: 'settings',
                          query: { page: 'security' },
                        });
                      }
                    "
                  >
                    <Lucide icon="Lock" class="w-4 h-4 mr-2" />
                    Reset Password
                  </Menu.Item> -->
                  <!-- <Menu.Divider /> -->
                  <Menu.Item
                    @click="
                      () => {
                        router.push({
                          name: 'settings',
                        });
                      }
                    "
                  >
                    <Lucide icon="Users" class="w-4 h-4 mr-2" />
                    Account Details
                  </Menu.Item>
                  <Menu.Item
                    @click="
                      () => {
                        authStore.logout();
                        // router.push({
                        //   name: 'settings',
                        //   query: { page: 'security' },
                        // });
                      }
                    "
                  >
                    <Lucide icon="Power" class="w-4 h-4 mr-2" />
                    Logout
                  </Menu.Item>
                </Menu.Items>
              </Menu>
            </div>
            <ActivitiesPanel
              :activitiesPanel="activitiesPanel"
              :setActivitiesPanel="setActivitiesPanel"
            />
            <NotificationsPanel
              :notificationsPanel="notificationsPanel"
              :setNotificationsPanel="setNotificationsPanel"
            />
            <SwitchAccount
              :switchAccount="switchAccount"
              :setSwitchAccount="setSwitchAccount"
            />
            <!-- END: Notification & User Menu -->
          </div>

          <!-- BEGIN: Breadcrumb -->
          <Breadcrumb light
              class="mobile-breadcrumb flex-1 xl:hidden"
              :class="{ 'mobile-breadcrumb--top-bar--active': topBarActive }"
          >
            <Breadcrumb.Link
                v-for="(item, index) in computedBreadcrumbs"
                :key="item.label + '-' + item.active"
                :to="item.link"
                :active="item.active"
                :index="index"
            >
              {{ item.label }}
            </Breadcrumb.Link>
          </Breadcrumb>

          <!-- END: Breadcrumb -->
        </div>

      </div>


    </div>
    <div
      :class="[
        'transition-[margin,width] duration-100 xl:pl-3.5 pt-[54px] pb-16 relative z-10 group mode',
        { 'xl:ml-[275px]': !compactMenu.value },
        { 'xl:ml-[91px]': compactMenu.value },
        { 'mode--light': !topBarActive && route.name !== 'ticketing-orders-bulk-import' && route.name !== 'ticketing-orders-bulk-send-email' },
      ]"
    >
      <div class="px-5 mt-16">

        <div class="container">

          <RouterView />
        </div>
      </div>
    </div>

  </div>
</template>


<style scoped>

.logo-wrapper {
  width:42px;
  position:relative;
  right:4px;
}
.logo path {
  fill:black !important;
}

.mobile-breadcrumb {
  transition:0.3s;
  opacity:1;
  &--top-bar--active {
    opacity:0;
  }
}

</style>